import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Auth from 'modules/Auth';
import { transformBookingStatusCode } from 'components/pages/DealWithLines/common/transformPostData';
import {
  combineProposalAllocationAndDealSummary,
  transformAvailability,
} from 'components/pages/DealWithLines/DealLines/transformDealDetails';
import {
  getDealWithLineFormData,
  getDealData,
  getLineFormData,
} from 'components/pages/DealWithLines/DealLines/transformPostData';
import { Store } from 'components/common/types/Store.types';
import {
  createDeal,
  checkAllocate,
  getAllocatedDeal,
  getDealProcessingStatus,
  approveDeal,
  rejectDeal,
  cancelDeal,
  reserveDeal,
  editDeal,
  updateLine,
  stopLine,
  createLine,
  proposeDeal,
} from 'modules/api/programmaticDeal';
import { getDealDetails } from 'store/dealManagement/actions';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import { FeatureFlags } from 'components/common/types/Features.types';
import {
  LineAllocationData,
  Allocation,
  DealProcessingStatuses,
  DealStatus,
  Availability,
  PreservedAvailability,
  FrontEndType,
  Line,
} from 'components/common/types/Deal.types';
import { PermissionsEnum } from 'consts/permissions';
import { AppDispatch } from 'store';
import {
  changeDealCurrentLineData,
  changeDealData,
  changeDealLineAllocatedImpressions,
  changeFormParams,
  clearDisclaimer,
  hideDisclaimer,
  removeTemporaryDealId,
  setIsFetchingAdvancedAvailabilityReport,
  showDisclaimer,
  updatePlannerSettings,
} from 'store/dealManagement/reducer';
import {
  getIsAnyLineFetchingAvailability,
  getIsProgrammaticDealLevelChange,
  getIsProgrammaticLineLevelChange,
} from 'store/dealManagement/selectors';
import { getIsReseller } from 'store/publisher/selectors';
import { addToastNotification, clearNotification } from 'store/notification/reducer';
import { isFrameCountExceedsLimitError } from 'store/notification/utils';
import { NAVIGATION_PATHS } from 'consts/navigation';
import { getAvailableAssets } from 'store/dealManagement/selectors/availabilityAssets';
import { getBackupLines } from 'store/dealManagement/selectors/getBackupLines';
import { getDealId } from 'store/dealManagement/selectors/getDealId';
import { getCurrentLine } from 'store/dealManagement/selectors/getCurrentLine';
import { getDealType } from 'store/dealManagement/selectors/getDealType';
import { getIsNewDeal } from 'store/dealManagement/selectors/getIsNewDeal';
import { getCampaignPlannerMode } from 'store/dealManagement/selectors/getCampaignPlannerMode';
import { resetServicesChanged } from 'store/nonMediaServices/reducer';
import { useCheckProgrammaticMandatoryFields } from './useCheckProgrammaticMandatoryFields';
import { BookingCode } from './usePlannerActions.types';
import useCommonPlannerActions from './useCommonPlannerActions';

interface UpdateDealHandlerCompleteParams {
  allocation?: Allocation;
  lineId?: string;
  dealId?: string;
}

interface UseProgrammaticPlannerActions {
  dealStatusComplete: (result: Allocation) => Promise<void>;
  checkAllocateHandler: () => Promise<void>;
  updateDealDetails: (campaignId: string) => Promise<void>;
  saveDeal: (bookingCode: BookingCode) => void;
  isSavingDisabled: boolean;
  isReadOnly: boolean;
  areButtonsHidden: boolean;
  statusConditions: {
    showConfirm: boolean;
    showSendForApproval: boolean;
    showReject: boolean;
    showSave: boolean;
    showCancel: boolean;
    showTerminate: boolean;
  };
}

const useProgrammaticPlannerActions = (): UseProgrammaticPlannerActions => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const commonDeal = useSelector((state: Store) => state.dealManagement.commonDeal);
  const isActionsDisabled = useSelector((state: Store) => state.dealManagement.isActionsDisabled);
  const isNewDeal = useSelector(getIsNewDeal);
  const isFetchingAvailability = useSelector(getIsAnyLineFetchingAvailability);
  const programmatic = useSelector((state: Store) => state.dealManagement.programmatic);
  const isCpmCampaignLevel = useSelector((state: Store) => state.dealManagement.isCpmCampaignLevel);
  const lines = useSelector(getBackupLines);
  const dealType = useSelector(getDealType);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const dealId = useSelector(getDealId);
  const campaignPlannerMode = useSelector(getCampaignPlannerMode);
  const backupDealSummary = useSelector((state: Store) => state.dealManagement.backupFormData.commonDeal.summary);
  const summary = useSelector((state: Store) => state.dealManagement.commonDeal.summary);
  const currentLine = useSelector(getCurrentLine);
  const cpm = useSelector((state: Store) => state.dealManagement.commonDeal.cpm);
  const lineId = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.id);
  const assets = useSelector(getAvailableAssets);
  const selectedNonMediaServices = useSelector((state: Store) => state.nonMediaServices.selectedNonMediaServices);
  const areServicesChanged = useSelector((state: Store) => state.nonMediaServices.areServicesChanged);
  const environmentId = useSelector((state: Store) => state.environment.environmentId);
  const isReseller = useSelector(getIsReseller);

  const { cancelFunctions, handleObjectives } = useCommonPlannerActions();

  const { areAllMandatoryFieldsFilled } = useCheckProgrammaticMandatoryFields();
  const isDealLevelChange = useSelector(getIsProgrammaticDealLevelChange);
  const isLineLevelChange = useSelector(getIsProgrammaticLineLevelChange);

  const isProperPendingApproval = bookingStatusCode === DealStatus.PENDING_APPROVAL;
  const isProperApproved = bookingStatusCode === DealStatus.APPROVED;
  const isProperRejected = bookingStatusCode === DealStatus.REJECTED;
  const isProperLive = bookingStatusCode === DealStatus.LIVE;

  const hasPermissionDealConfirm = Auth.hasPermission(PermissionsEnum.DEAL_CONFIRM);
  const hasPermissionDealReject = Auth.hasPermission(PermissionsEnum.DEAL_REJECT);

  const showConfirm = hasPermissionDealConfirm && (isNewDeal || isProperPendingApproval);
  const showSendForApproval = Auth.hasPermission(PermissionsEnum.DEAL_PROPOSE) && (isNewDeal || isProperRejected);
  const showSave =
    hasPermissionDealConfirm &&
    (isDealLevelChange || isLineLevelChange) &&
    (isProperPendingApproval || isProperApproved || isProperRejected || isProperLive);
  const showReject = hasPermissionDealReject && bookingStatusCode === DealStatus.PENDING_APPROVAL;
  const showCancel = hasPermissionDealReject && bookingStatusCode === DealStatus.APPROVED;
  const showTerminate = hasPermissionDealReject && bookingStatusCode === DealStatus.LIVE;

  const isSavingDisabled =
    isActionsDisabled || isFetchingAvailability || !areAllMandatoryFieldsFilled || !assets?.length;
  const isReadOnly = [DealStatus.CANCELLED, DealStatus.TERMINATED, DealStatus.ENDED].includes(bookingStatusCode);
  const areButtonsHidden =
    !showCancel && !showTerminate && !showReject && !showConfirm && !showSave && !showSendForApproval;
  const hasAdsDealLevelCPMEnabled = useHasFeatureAccess(FeatureFlags.DEAL_LEVEL_CPM);
  const hasNonGuaranteedExtendedTargetEnabled = useHasFeatureAccess(FeatureFlags.NON_GUARANTEED_EXTENDED_TARGET);

  const createOrUpdateLine = useCallback(
    (lineData: unknown) => {
      return lineId
        ? updateLine(cancelFunctions, lineData, { dealId, lineId })
        : createLine(cancelFunctions, lineData, { dealId }, commonDeal.currentLine.frontEndType);
    },
    [cancelFunctions, commonDeal.currentLine.frontEndType],
  );

  const updateDealDetails = async (campaignID: string): Promise<void> => {
    if (dealId) {
      await dispatch(
        getDealDetails(
          dealId,
          cancelFunctions,
          navigate,
          hasAdsDealLevelCPMEnabled,
          hasNonGuaranteedExtendedTargetEnabled,
        ),
      );
    } else if (campaignID) {
      navigate(`/planner/programmatic/${campaignID}/${campaignPlannerMode}`, { replace: true });
    } else {
      navigate(NAVIGATION_PATHS.PROGRAMMATIC, { replace: true });
    }
  };

  const dealStatusComplete = async (result: Allocation): Promise<void> => {
    dispatch(
      addToastNotification({
        description: `Campaign saved successfully - ${result.campaignID ?? dealId}`,
        type: 'Success',
        timeout: 2000,
      }),
    );
    dispatch(changeFormParams({ isForecastedAllocation: false }));
    dispatch(changeFormParams({ hasUnsavedProposal: false }));
    dispatch(removeTemporaryDealId());
    dispatch(updatePlannerSettings({ isClientSideAssetFiltering: false }));

    await updateDealDetails(result.campaignID);
  };

  const lineStatusComplete = async (lineLevelId: string, dealLevelId: string): Promise<void> => {
    if (!lineLevelId || !dealLevelId) return;

    dispatch(
      addToastNotification({ description: `Line changes saved Successfully - ${lineLevelId}`, type: 'Success' }),
    );
    dispatch(changeFormParams({ isForecastedAllocation: false }));
    dispatch(changeFormParams({ hasUnsavedProposal: false }));
    dispatch(changeFormParams({ isEditingDisabled: false }));
    dispatch(removeTemporaryDealId());
    dispatch(updatePlannerSettings({ isClientSideAssetFiltering: false }));

    await updateDealDetails(dealLevelId);
  };

  const dealAndLineStatusComplete = async (
    allocation: Allocation,
    lineLevelId: string,
    dealLevelId: string,
  ): Promise<void> => {
    dispatch(
      addToastNotification({
        description: `Deal ${allocation.campaignID ?? dealId} and line ${lineLevelId} saved Successfully`,
        type: 'Success',
      }),
    );
    dispatch(changeFormParams({ isForecastedAllocation: false }));
    dispatch(changeFormParams({ hasUnsavedProposal: false }));
    dispatch(changeFormParams({ isEditingDisabled: false }));
    dispatch(removeTemporaryDealId());
    dispatch(updatePlannerSettings({ isClientSideAssetFiltering: false }));

    await updateDealDetails(dealLevelId);
  };

  const handleStatusComplete = async (payload: UpdateDealHandlerCompleteParams): Promise<void> => {
    if (isDealLevelChange && isLineLevelChange) {
      await dealAndLineStatusComplete(payload.allocation!, payload.lineId || lineId, payload.dealId || dealId);
    } else if (isDealLevelChange) {
      await dealStatusComplete(payload.allocation!);
    } else if (isLineLevelChange) {
      await lineStatusComplete(payload.lineId || lineId, payload.dealId || dealId);
    }
  };

  const handleErrorMessages = (errors: { lineId: string; message: string }[]): void => {
    const currentLineError = errors.find((error) => error.lineId === lineId) || errors[0];
    const lineName = lines.find(({ id }) => id === currentLineError.lineId)?.name || currentLine.name;

    if (currentLineError) {
      dispatch(addToastNotification({ description: `${lineName}: ${currentLineError.message}`, type: 'Failure' }));
    }
  };

  const handlePollStatus = async (
    result: Allocation,
    pollCompleteHandler: (result: Allocation) => Promise<void>,
    isRequestAfterPoll = false,
    isEditingDisabledHandler = false,
  ): Promise<void> => {
    const poll = await getDealProcessingStatus(result.campaignID || dealId, cancelFunctions);

    const handleProcessingError = (messages?: { lineId: string; message: string }[]): false => {
      if (messages) {
        handleErrorMessages(messages);
      }

      if (!isEditingDisabledHandler) {
        dispatch(changeFormParams({ isEditingDisabled: false }));
      }
      return false;
    };

    const shouldKeepPolling = (processingStatus: string): boolean =>
      processingStatus === DealProcessingStatuses.STILL_PROCESSING;

    let handlerCompletePromise: Promise<void> | null = null;

    const executeCompleteHandler = (): void => {
      if (!isRequestAfterPoll && !isEditingDisabledHandler) {
        dispatch(changeFormParams({ isEditingDisabled: false }));
      }
      handlerCompletePromise = pollCompleteHandler(result);
    };

    await poll(
      ({
        data: { processingStatus, messages },
      }: {
        data: {
          processingStatus: string;
          campaignID: string;
          messages?: { lineId: string; message: string }[];
        };
      }): boolean | void => {
        if (processingStatus === DealProcessingStatuses.ERROR) {
          return handleProcessingError(messages);
        }

        if (shouldKeepPolling(processingStatus)) {
          return true;
        }

        executeCompleteHandler();
        return false;
      },
      1000,
    );

    if (handlerCompletePromise) await handlerCompletePromise;
  };

  const runDealRequest = async (
    requestHandler: () => Promise<Allocation>,
    pollCompleteHandler: (result: Allocation) => Promise<void>,
  ): Promise<void> => {
    dispatch(changeFormParams({ isEditingDisabled: true }));
    dispatch(
      addToastNotification({
        header: 'We are validating your solution',
        timeout: 2000,
        type: 'Info',
      }),
    );

    try {
      const result = await requestHandler();

      await handlePollStatus(result, pollCompleteHandler, false, true);
    } catch {
      // eslint-disable-line no-empty
    } finally {
      dispatch(changeFormParams({ isEditingDisabled: false }));
    }
  };

  const handleDealRequest = async (
    requestHandler: () => Promise<Allocation>,
    pollCompleteHandler: (result: Allocation) => Promise<void>,
  ): Promise<void> => {
    await runDealRequest(requestHandler, pollCompleteHandler);

    dispatch(removeTemporaryDealId());
  };

  const removePoiPointsFromLine = (line: Line): Line => ({
    ...line,
    proximity: {
      ...commonDeal.currentLine.proximity,
      plannerPoi: commonDeal.currentLine.proximity.plannerPoi.map((poi) => ({ ...poi, poiPoints: [] })),
      openStreetMapPoi: commonDeal.currentLine.proximity.openStreetMapPoi.map((poi) => ({
        ...poi,
        poiPoints: [],
      })),
    },
  });

  /* eslint-disable sonarjs/cognitive-complexity */
  const updateDealHandler = useCallback(
    async (
      onCompleteHandler?: (params: UpdateDealHandlerCompleteParams) => Promise<void>,
      catchHandler = () => undefined,
    ): Promise<void> => {
      const dealData = {
        ...getDealData({
          ...commonDeal,
          ...programmatic,
          cpm: isCpmCampaignLevel ? commonDeal.cpm : null,
          hasAdsDealLevelCPMEnabled,
        }),
        enableLineLevelTrading: !isCpmCampaignLevel,
        bookingStatusCode: bookingStatusCode === DealStatus.DRAFT ? DealStatus.DRAFT : undefined,
      };

      const lineData = getLineFormData(
        {
          ...commonDeal,
          ...programmatic,
          currentLine: {
            ...removePoiPointsFromLine(commonDeal.currentLine),
            cpm: isCpmCampaignLevel ? undefined : commonDeal.currentLine.cpm,
          },
          ...(selectedNonMediaServices?.length ? { selectedNonMediaServices } : {}),
        },
        isCpmCampaignLevel,
        environmentId,
        lineId,
        areServicesChanged,
      );

      dispatch(changeFormParams({ isEditingDisabled: true }));
      cancelFunctions.FETCH_ADVANCED_DIRECT_SALES_AVAILABILITY_REPORT_EXPORT_ID?.();
      cancelFunctions.FETCH_ADVANCED_DIRECT_SALES_AVAILABILITY?.();
      dispatch(setIsFetchingAdvancedAvailabilityReport(false));
      dispatch(
        addToastNotification({
          header: 'We are validating your solution',
          timeout: 2000,
          type: 'Info',
        }),
      );

      try {
        if (isDealLevelChange) {
          const editDealResponse: Allocation = await editDeal(cancelFunctions, dealId, dealData);

          await handlePollStatus(
            editDealResponse,
            async (dealLevelAllocation) => {
              try {
                if (isLineLevelChange) {
                  await createOrUpdateLine(lineData);

                  await handlePollStatus(editDealResponse, async (lineLevelAllocation) =>
                    onCompleteHandler?.({
                      allocation: lineLevelAllocation,
                      dealId,
                      lineId,
                    }),
                  );

                  dispatch(resetServicesChanged());

                  return;
                }

                onCompleteHandler?.({
                  allocation: dealLevelAllocation,
                });
              } catch {
                catchHandler();
                dispatch(changeFormParams({ isEditingDisabled: false }));
              }
            },
            isLineLevelChange,
          );

          return;
        }

        if (!isDealLevelChange && isLineLevelChange) {
          const result = await createOrUpdateLine(lineData);

          await handlePollStatus(
            result,
            async (allocation) =>
              onCompleteHandler?.({
                allocation,
                dealId,
                lineId: lineId || result.dealIds[result.dealIds.length - 1],
              }),
            isLineLevelChange,
          );

          dispatch(resetServicesChanged());

          return;
        }
      } catch (error) {
        catchHandler();
        dispatch(changeFormParams({ isEditingDisabled: false }));
        if (!isFrameCountExceedsLimitError(error)) {
          dispatch(
            addToastNotification({
              description: 'Failed to save deal changes',
              type: 'Failure',
            }),
          );
        }
      }
    },
    [
      commonDeal,
      programmatic,
      isLineLevelChange,
      isDealLevelChange,
      isCpmCampaignLevel,
      bookingStatusCode,
      hasAdsDealLevelCPMEnabled,
      dealId,
      environmentId,
      selectedNonMediaServices,
      areServicesChanged,
    ],
  );
  /* eslint-enable sonarjs/cognitive-complexity */

  const updateDeal = async (catchHandler: Function): Promise<void> => {
    await updateDealHandler(handleStatusComplete, catchHandler);
  };

  const checkAllocatePollComplete = async ({ campaignID }: Allocation): Promise<void> => {
    try {
      const currentLineAllocationProposal: LineAllocationData = await getAllocatedDeal(
        campaignID,
        cancelFunctions,
        dealType,
        lines,
      );

      handleObjectives(currentLineAllocationProposal);
      dispatch(showDisclaimer());

      dispatch(
        changeDealLineAllocatedImpressions(
          transformAvailability(currentLineAllocationProposal) as Availability & PreservedAvailability,
        ),
      );
      dispatch(
        changeDealData({
          summary: combineProposalAllocationAndDealSummary(
            isNewDeal,
            !currentLine.lineId,
            currentLine.preservedAllocation,
            currentLineAllocationProposal,
            summary,
            backupDealSummary,
            dealType,
            hasNonGuaranteedExtendedTargetEnabled,
          ),
        }),
      );
      dispatch(changeFormParams({ temporaryDealId: campaignID, isForecastedAllocation: true }));
      dispatch(
        changeDealCurrentLineData({
          deliveredImpressions: currentLineAllocationProposal.deliveredImpressions,
          isCurrentLineWithProposalAllocation: true,
        }),
      );
      dispatch(changeFormParams({ hasUnsavedProposal: true }));
    } catch {
      dispatch(clearNotification());
    }
  };

  const checkAllocateHandler = useCallback(async (): Promise<void> => {
    dispatch(hideDisclaimer());
    dispatch(clearDisclaimer());
    dispatch(changeFormParams({ isFetchingAllocation: true }));

    const formData = getDealWithLineFormData(
      {
        ...commonDeal,
        ...programmatic,
        cpm: isCpmCampaignLevel ? cpm : null,
        bookingStatusCode: DealStatus.PROPOSAL,
        hasAdsDealLevelCPMEnabled,
        ...(!isReseller && { environmentId }),
        ...(dealId ? { overiddenCampaignId: dealId } : { overiddenCampaignId: null }),
        ...(selectedNonMediaServices?.length ? { selectedNonMediaServices } : {}),
      },
      areServicesChanged,
    );

    try {
      await runDealRequest(() => checkAllocate(cancelFunctions, formData), checkAllocatePollComplete);
    } finally {
      dispatch(changeFormParams({ isFetchingAllocation: false }));
    }
  }, [
    commonDeal,
    programmatic,
    isCpmCampaignLevel,
    hasAdsDealLevelCPMEnabled,
    dealId,
    environmentId,
    selectedNonMediaServices,
    cpm,
    areServicesChanged,
  ]);

  const createUpdateAndActionHandler = (
    actionHandler: (onCompleteHandler: (result: Allocation) => Promise<void>) => Promise<void>,
  ) => {
    return async (catchHandler?: Function): Promise<void> => {
      if (!isDealLevelChange && !isLineLevelChange) {
        await actionHandler(dealStatusComplete);
        return;
      }

      await updateDealHandler(async (editDealResult) => {
        await actionHandler(async (allocation: Allocation) => {
          await handleStatusComplete({ ...editDealResult, allocation });
        });
      }, catchHandler);
    };
  };

  const submitNewDeal = useCallback(
    async (bookingCode: BookingCode): Promise<void> => {
      const formData = getDealWithLineFormData(
        {
          ...commonDeal,
          ...programmatic,
          bookingStatusCode: transformBookingStatusCode(bookingCode ?? ''),
          hasAdsDealLevelCPMEnabled,
          currentLine: removePoiPointsFromLine(commonDeal.currentLine),
          ...(!isReseller && { environmentId }),
          ...(selectedNonMediaServices?.length ? { selectedNonMediaServices } : {}),
        },
        areServicesChanged,
      );

      await handleDealRequest(() => createDeal(cancelFunctions, formData, FrontEndType.PLANNER), dealStatusComplete);

      dispatch(resetServicesChanged());
    },
    [
      commonDeal,
      programmatic,
      hasAdsDealLevelCPMEnabled,
      currentLine,
      environmentId,
      selectedNonMediaServices,
      areServicesChanged,
    ],
  );

  const proposeDealHandler = async (onCompleteHandler: (result: Allocation) => Promise<void>): Promise<void> => {
    await handleDealRequest(() => proposeDeal(cancelFunctions, { dealId }), onCompleteHandler);
  };

  const approveDealHandler = async (onCompleteHandler: (result: Allocation) => Promise<void>): Promise<void> => {
    await handleDealRequest(() => approveDeal(cancelFunctions, { dealId }), onCompleteHandler);
  };

  const rejectDealHandler = async (onCompleteHandler: (result: Allocation) => Promise<void>): Promise<void> => {
    await handleDealRequest(() => rejectDeal(cancelFunctions, { dealId }), onCompleteHandler);
  };

  const terminateLineHandler = async (): Promise<void> => {
    await handleDealRequest(() => stopLine(cancelFunctions, { dealId, lineId }), dealStatusComplete);
  };

  const cancelDealHandler = async (onCompleteHandler: (result: Allocation) => Promise<void>): Promise<void> => {
    await handleDealRequest(() => cancelDeal(cancelFunctions, { dealId }), onCompleteHandler);
  };

  const reserveDealHandler = (): Promise<void> =>
    handleDealRequest(() => reserveDeal(cancelFunctions, { dealId }), dealStatusComplete);

  const updateAndProposeDeal = createUpdateAndActionHandler(proposeDealHandler);

  const updateAndApproveDeal = createUpdateAndActionHandler(approveDealHandler);

  const updateAndRejectDeal = createUpdateAndActionHandler(rejectDealHandler);

  const updateAndCancelDeal = createUpdateAndActionHandler(cancelDealHandler);

  const saveDeal = useCallback(
    (bookingCode: BookingCode, catchHandler = () => undefined) => {
      dispatch(hideDisclaimer());

      if (!dealId) {
        submitNewDeal(bookingCode);
        return;
      }

      switch (bookingCode) {
        case DealStatus.DRAFT:
          updateDeal(catchHandler);
          break;
        case DealStatus.PENDING_APPROVAL:
          updateAndProposeDeal(catchHandler);
          break;
        case DealStatus.APPROVED:
          updateAndApproveDeal(catchHandler);
          break;
        case DealStatus.REJECTED:
          updateAndRejectDeal(catchHandler);
          break;
        case DealStatus.TERMINATED:
          terminateLineHandler();
          break;
        case DealStatus.CANCELLED:
          updateAndCancelDeal(catchHandler);
          break;
        case DealStatus.RESERVED:
          reserveDealHandler();
          break;
        default:
          updateDeal(catchHandler);
      }
    },
    [updateDealHandler],
  );

  return {
    dealStatusComplete,
    updateDealDetails,
    areButtonsHidden,
    isSavingDisabled,
    isReadOnly,
    statusConditions: {
      showConfirm,
      showSendForApproval,
      showSave,
      showReject,
      showCancel,
      showTerminate,
    },
    checkAllocateHandler,
    saveDeal,
  };
};

export default useProgrammaticPlannerActions;
